/* eslint-disable no-restricted-syntax -- Rettentő legacy. */
/* eslint-disable custom/max-top-level-functions-per-file -- Legacy. */

import { is } from '@common/is/is'

import Logger from '@common/Logger/Logger'

import CloseButton from '../CloseButton/CloseButton'

import type { IModalOptions } from '../definitions'
import getBootstrapApi from '../getBootstrapApi'

import './Modal.css'

const log = new Logger('MODAL')

export interface IProps {
  readonly body: HTMLElement | DocumentFragment | null
  readonly header?: HTMLElement | null
  readonly footer?: HTMLElement | null
  readonly title?: string
  readonly size?: 'sm' | 'default' | 'lg' | 'xl' | 'xxl' | 'fullscreen'
  readonly className?: string
  readonly isCentered?: boolean
  readonly onClose?: TVoidCallback

  /** A natív bootstrap modal beállítási lehetőségek. */
  readonly options?: IModalOptions

  /**
   * Általában akkor használunk custom zIndexet, ha egymásra nyíló modalokkal dolgozunk és szeretnénk,
   * hogy az egyik a másik felé nyíljon.
   * Ilyenre példa, amikor Modal-ban van megerősítő, ConfirmModal.
   * A ConfirmModal mindig magasabb z-n helyezkedik el, mint egy alap modal.
   */
  readonly zIndex?: number
}

let actualModalNumber = 0

/** Az aktuális modal ID-ját adja vissza. */
function getActualModalId (): string {
  return `modal-${ actualModalNumber }`
}

/** Visszaadja az aktuális modal elemet, vagy null-t. */
export function getActualModal (): HTMLElement | null {
  const selector = `#${ getActualModalId() }`
  const modal    = document.querySelector<HTMLElement>(selector)

  log.dev('getActualModal', selector, modal)

  return modal
}

/**
 * Az aktuális modal-t bezáró függvény.
 * Kintről is hívható, de az összes bezáró gombra is ez van rákötve.
 */
export function closeActualModal (): void {
  const modalEl = getActualModal()

  if (!modalEl) {
    log.dev('closeActualModal', 'Sikertelen bezárás. Nem található modal.')

    return
  }

  const actModal = getBootstrapApi().Modal.getInstance(modalEl)

  actModal.hide()

  log.dev('closeActualModal', 'Sikeres bezárás.')
}

document.body.addEventListener('hidden.bs.modal', () => {
  const modalEl = getActualModal()

  if (!modalEl) {
    log.dev('hidden.bs.modal', 'Sikertelen eltávolítás. Nem található modal.')

    return
  }

  const actModal = getBootstrapApi().Modal.getInstance(modalEl)

  actModal.dispose()

  if (is.instanceOf(modalEl, HTMLElement)) {
    modalEl.remove()
  }

  // Igy lehet több modal is egymáson és a closeActual mindig be tudja zárni az előzőt is.
  actualModalNumber--

  log.dev('hidden.bs.modal', 'Sikeres eltávolítás! Aktuális modalIndex: ', actualModalNumber)
})

interface ICloseButtonProps {
  readonly onClick?: TVoidCallback
}

/**
 * Az aktuális modalt bezáró gomb factory-ja.
 * @see {@link https://v5.getbootstrap.com/docs/5.0/components/modal/}
 * @param props - A bezárógomb beállításai {@link ICloseButtonProps}.
 */
export function ModalCloseButton (props: ICloseButtonProps): HTMLElement {
  return (
    <button
      className={ [ 'btn', 'btn-secondary' ] }
      type="button"
      onClick={ () => {
        if (props.onClick) {
          props.onClick()
        }

        closeActualModal()
      } }
    >
      Bezárás
    </button>
  )
}

/**
 * Bootstrap-alapú ablak factory-ja.
 * @param props - JSX props {@link IProps}.
 */
export default function Modal (props: IProps): void {
  const {
    body,
    className = '',
    footer,
    header,
    isCentered,
    onClose,
    options   = {},
    size      = 'default',
    title     = '?',
    zIndex
  } = props

  actualModalNumber++

  const modalId = getActualModalId()

  log.dev('Új modal létrehozva.', 'Aktuális modalIndex:', actualModalNumber, 'id:', modalId)

  const modalEl: HTMLDivElement = (
    <div
      id={ modalId }
      className={ [ 'modal', className ] }
      role="dialog"
    >
      <div className={ [ 'modal-dialog', `modal-${ size }`, isCentered ? 'modal-dialog-centered' : ''  ] }>
        <div className="modal-content">
          {
            is.undefined(header)
              ? (
                <div className="modal-header">
                  <h5 className="modal-title" dangerouslySetInnerHTML={ { __html: title } } />
                  <CloseButton onClick={ closeActualModal } />
                </div>
              )
              : header
          }

          { body }

          {
            is.undefined(footer)
              ? (
                <div className="modal-footer">
                  <ModalCloseButton />
                  <button className={ [ 'btn', 'btn-primary' ] } type="button">Rendben</button>
                </div>
              )
              : footer
          }
        </div>
      </div>
    </div>
  )

  if (is.number(zIndex)) {
    modalEl.style.zIndex = `${ zIndex }`
  }

  document.body.appendChild(modalEl)

  const { Modal } = getBootstrapApi()
  const myModal = new Modal(modalEl, options)

  myModal.show()

  // Ha van custom zIndex, akkor a modal backdropját is be kell állítani.
  if (is.number(zIndex)) {
    const backdrop = modalEl.nextSibling

    if (is.instanceOf(backdrop, HTMLElement)) {
      backdrop.style.zIndex = `${ zIndex - 1 }`
    }
  }

  if (onClose) {
    modalEl.addEventListener('hidden.bs.modal', () => {
      onClose()
    })
  }
}
